import React, { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Spinner,
  Alert,
} from "reactstrap";
import { APIClient } from "../../helpers/api_helper";
import { translateApiError } from "../../helpers/translateApiError";
import BreadCrumb from "../../components/Common/BreadCrumb";
import SmsState from "../../components/Common/SmsState";
import formatPersonName from "../../helpers/formatPersonName";
import { formatDateTime } from "../../helpers/dateFormat";
import "./smsLogDetail.css";

const api = new APIClient();

const SmsLogDetail = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const history = useHistory();
  const [smsLog, setSmsLog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSmsLogDetail = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const url = `${process.env.REACT_APP_API_BASE_URL}/admin/sms-logs/${id}`;
      const response = await api.get(url);
      if (response?.data) {
        setSmsLog(response.data);
      }
    } catch (err) {
      console.error("Error fetching SMS log detail:", err);

      // Only set error if it's not an auto logout error
      if (!err?.autoLogout) {
        setError(translateApiError(err, t("smsLogs.detail.loadError")));
      }
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    fetchSmsLogDetail();
  }, [fetchSmsLogDetail]);

  // Back to the list page and filters the user came from.
  const handleBack = () => {
    history.push(history.location.state?.from || "/sms-logs");
  };

  const getStatusState = (status) => {
    // Treat null/empty as Pending
    if (!status) {
      return "Pending";
    }
    const statusLower = status.toLowerCase();
    if (statusLower === "delivered" || statusLower === "success") {
      return "Delivered";
    } else if (statusLower === "pending" || statusLower === "unknown") {
      return "Pending";
    } else if (statusLower === "not_sent") {
      return "NotSent";
    }
    return "Failed";
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("smsLogs.detail.pageTitle")}
            pageTitle={t("smsLogs.pageTitle")}
          />
          <div className="text-center mt-5">
            <Spinner color="primary" />
            <p className="mt-2">{t("smsLogs.detail.loading")}</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error || !smsLog) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb
            title={t("smsLogs.detail.pageTitle")}
            pageTitle={t("smsLogs.pageTitle")}
          />
          <Alert color="danger">
            {error || t("smsLogs.detail.notFound")}
            <Button color="link" onClick={handleBack} className="ms-2">
              {t("smsLogs.detail.goBack")}
            </Button>
          </Alert>
        </Container>
      </div>
    );
  }

  document.title = t("common.pageTitle", {
    page: t("smsLogs.detail.pageTitle"),
  });

  const recipientName =
    formatPersonName(smsLog.recipientsFirstName, smsLog.recipientsLastName) ||
    "-";

  // Not sent: translate our error code. Failed: EVERY8D's own message.
  const notSent = getStatusState(smsLog.deliveryStatus) === "NotSent";
  const reason = notSent
    ? t(`smsLogs.errorCodes.${smsLog.errorCode}`, {
        defaultValue: t("smsLogs.errorCodes.UNKNOWN"),
      })
    : smsLog.errorMessage;
  // EVERY8D rejected it: also show EVERY8D's own message under our reason
  const every8dMessage =
    notSent && smsLog.errorCode === "EVERY8D_REJECTED"
      ? smsLog.errorMessage
      : null;

  // [label, value] fields of the SMS information card, two per row:
  // who and when first, then IDs for troubleshooting below a divider
  const infoFields = [
    [
      t("smsLogs.columns.recipientName"),
      smsLog.contactId && smsLog.portalId ? (
        <a
          key="contact"
          href={`https://app.hubspot.com/contacts/${smsLog.portalId}/contact/${smsLog.contactId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {recipientName}
        </a>
      ) : (
        recipientName
      ),
    ],
    [t("smsLogs.columns.recipientPhone"), smsLog.phoneNumber || "-"],
    [
      t("smsLogs.detail.sentTime"),
      smsLog.sentAt ? formatDateTime(smsLog.sentAt, i18n.language) : "-",
    ],
    [
      t("smsLogs.detail.messageCost"),
      smsLog.reducePoint !== undefined && smsLog.reducePoint !== null
        ? smsLog.reducePoint
        : smsLog.pointsCost || "1",
    ],
  ];
  const referenceFields = [
    [t("smsLogs.columns.smsId"), smsLog.id],
    [
      t("smsLogs.columns.workflowId"),
      smsLog.workflowId ? (
        <a
          key="workflow"
          href={smsLog.workflowTitle}
          target="_blank"
          rel="noopener noreferrer"
        >
          {smsLog.workflowId}
        </a>
      ) : (
        "-"
      ),
    ],
    // Always both, so the fields stay the same. Our code is set when not
    // sent; EVERY8D's status code once EVERY8D received the message.
    [t("smsLogs.detail.errorCode"), smsLog.errorCode || "-"],
    [t("smsLogs.detail.statusCode"), smsLog.statusCode || "-"],
  ];

  const renderFields = (fields) => (
    <Row className="g-3">
      {fields.map(([label, value]) => (
        <Col key={label} xs={6}>
          <p className="text-muted mb-1 fs-13">{label}</p>
          <div className="sms-info-value">{value}</div>
        </Col>
      ))}
    </Row>
  );

  return (
    <div className="page-content sms-log-detail">
      <Container fluid>
        <BreadCrumb
          title={t("smsLogs.detail.pageTitle")}
          pageTitle={t("smsLogs.pageTitle")}
        />

        <Button
          color="link"
          onClick={handleBack}
          className="back-button p-0 mb-3"
        >
          <i className="ri-arrow-left-line"></i>
          <span>{t("common.actions.back")}</span>
        </Button>

        <Row className="g-4 align-items-start">
          {/* Message body */}
          <Col lg={7}>
            <Card className="mb-0">
              <CardHeader>
                <h5 className="card-title mb-0">
                  {t("smsLogs.detail.messageBody")}
                </h5>
              </CardHeader>
              <CardBody>
                <div className="sms-message-text">
                  {smsLog.body || smsLog.workflowMessage || "-"}
                </div>
              </CardBody>
            </Card>
          </Col>

          {/* SMS information */}
          <Col lg={5}>
            <Card className="mb-0">
              {/* Result first: status, then why it was not delivered */}
              <CardHeader>
                <SmsState
                  state={getStatusState(smsLog.deliveryStatus)}
                  size="Medium"
                />
                {reason && (
                  <div
                    className={`sms-reason mt-3 ${
                      notSent ? "sms-reason-not-sent" : ""
                    }`}
                    role="note"
                    aria-label={t("smsLogs.detail.reason")}
                  >
                    <i className="ri-error-warning-line"></i>
                    <span>
                      {reason}
                      {every8dMessage && (
                        <span className="d-block mt-1">
                          {t("smsLogs.detail.every8dMessage", {
                            message: every8dMessage,
                          })}
                        </span>
                      )}
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardBody>
                {renderFields(infoFields)}
                <hr className="my-3 border-light-subtle" />
                {renderFields(referenceFields)}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SmsLogDetail;
